import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { useToast } from "../hooks/use-toast";
import {
  Building2,
  ArrowRight,
  Mail,
  Lock,
  Loader2,
} from "lucide-react";
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../lib/api';
import bgImage from "../assets/auth-background.png";

// Simplified schema - Brightstone internal use only
const signupSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  fullName: z.string().min(2, "Full name must be at least 2 characters"),
});

type SignupValues = z.infer<typeof signupSchema>;

export default function Signup() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();

  const form = useForm<SignupValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: "",
      password: "",
      fullName: "",
    },
  });

  const onSubmit = async (data: SignupValues) => {
    setIsLoading(true);
    
    try {
      // Parse full name into first and last name
      const nameParts = data.fullName.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      // Register user - automatically assigned to Brightstone
      await apiClient.auth.register({
        email: data.email,
        password: data.password,
        first_name: firstName,
        last_name: lastName,
      });

      // Login after successful registration
      try {
        await login(data.email, data.password);
        console.log('✅ Auto-login successful');
        
        // Wait a moment for session to be established
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (loginErr: any) {
        console.error('⚠️ Auto-login failed, but registration was successful:', loginErr);
        throw new Error('Registration succeeded but login failed. Please log in manually.');
      }
      
      toast({
        title: "Account created!",
        description: "Welcome to Cityscrape. Redirecting to dashboard...",
      });
      
      setIsLoading(false);
      navigate('/');
    } catch (err: any) {
      console.error('❌ Registration error:', err);
      setIsLoading(false);
      toast({
        title: "Registration failed",
        description: err.response?.data?.detail || 'Registration failed. Please try again.',
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen w-full flex overflow-hidden">
      {/* Left Side - Form Area */}
      <div className="w-full lg:w-1/2 flex flex-col bg-background relative z-10">
        <div className="flex-1 flex flex-col justify-center items-center p-8 lg:p-12">
          <div className="w-full max-w-md space-y-6">
            {/* Header */}
            <div className="space-y-2 text-left">
              <div className="flex justify-start mb-6">
                <span className="text-2xl font-bold text-primary tracking-tight flex items-center gap-2">
                  <Building2 className="h-8 w-8" />
                  Cityscrape
                </span>
              </div>
              
              <h1 className="text-3xl font-bold tracking-tight text-foreground mb-2">
                Create your account
              </h1>
              <p className="text-muted-foreground mb-6">
                Sign up to access Canada's municipal planning data.
              </p>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                            <Input placeholder="name@company.com" className="pl-9" {...field} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                            <Input type="password" placeholder="Create a password" className="pl-9" {...field} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="fullName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Full Name</FormLabel>
                        <FormControl>
                          <Input placeholder="John Doe" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button 
                    type="submit" 
                    className="w-full h-11 font-medium text-base group" 
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      <>
                        Create account
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </Button>
                </form>
              </Form>
            </div>
          </div>
        </div>
        
        {/* Footer Link */}
        <div className="p-6 text-left text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-medium text-primary hover:underline underline-offset-4"
          >
            Sign in
          </Link>
        </div>
      </div>

      {/* Right Side - Content */}
      <div className="hidden lg:flex w-1/2 relative bg-muted text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={bgImage}
            alt="Background"
            className="w-full h-full object-cover opacity-40 mix-blend-multiply filter grayscale"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-neutral-900/80 to-neutral-900/90 mix-blend-multiply" />
          <div className="absolute inset-0 bg-primary/10 mix-blend-overlay" />
        </div>

        <div className="relative z-10 flex flex-col justify-center w-full p-12 xl:p-20 h-full">
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl xl:text-5xl font-bold leading-tight mb-6 text-white">
                Speed and Clarity for Every Project
              </h2>
              <p className="text-lg text-neutral-300 max-w-md leading-relaxed">
                Instant access to open, unified planning information so you can
                make faster, clearer, more informed development and acquisition
                decisions.
              </p>
            </div>

            <div className="space-y-4 pt-4">
              {[
                "Save Time on Research",
                "Stay Ahead of Policy Changes",
                "Reduce Project Risk",
                "Make Faster Decisions",
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-3 text-neutral-200"
                >
                  <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center text-primary border border-primary/30">
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
